'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'
import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'
import { prepareSubmissionData } from '@/utilities/formSubmission'
import { ChevronsRight } from 'lucide-react'
import { cn } from '@/utilities/ui'
import BlockWrapper from '@/components/BlockWrapper'
import Image from 'next/image'
import TurnstileWidget from '@/components/TurnstileWidget'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType
  introContent?: SerializedEditorState
  className?: string
  formHeading?: SerializedEditorState
  formSubheading?: SerializedEditorState
  image?:
    | {
        url?: string
        caption?: string
      }
    | string
  title?: SerializedEditorState // Add title to FormBlockType
}

export const FormBlock2: React.FC<
  {
    id?: string
    hideImageSection?: boolean
    bareForm?: boolean
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
    className,
    title, // Destructure title prop
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields as any,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const [tsToken, setTsToken] = useState<string | null>(null)
  const [tsResetKey, setTsResetKey] = useState<number>(0)
  const [formTouched, setFormTouched] = useState(false)
  const router = useRouter()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = prepareSubmissionData(data as unknown as Record<string, unknown>)

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              'cf-turnstile-response': tsToken,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            // Force new token for next attempt
            setTsToken(null)
            setTsResetKey((v) => v + 1)

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          // Prepare fresh token if staying on page
          setTsToken(null)
          setTsResetKey((v) => v + 1)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
          setTsToken(null)
          setTsResetKey((v) => v + 1)
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, tsToken],
  )

  if (props.bareForm) {
    return (
      <div
        className={`flex-1 bg-white/80 rounded-2xl p-6 shadow ${
          formFromProps?.fields?.length === 3 ? 'max-w-md mx-auto' : 'max-w-2xl'
        } min-w-[400px] px-10`}
      >
        <FormProvider {...formMethods}>
          {/* Render title if provided */}
          {title && (
            <div className="mb-4 text-center">
              <RichText
                data={title}
                enableGutter={false}
                enableProse={false}
                className="text-2xl font-bold text-foreground"
              />
            </div>
          )}
          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            <RichText data={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && (
            <p className="text-lg text-muted-foreground">Loading, please wait...</p>
          )}
          {error && (
            <div className="text-destructive font-bold">{`${error.status || '500'}: ${error.message || ''}`}</div>
          )}
          {!hasSubmitted && (
            <form
              id={formID}
              onSubmit={handleSubmit(onSubmit)}
              onFocusCapture={() => {
                if (!formTouched) {
                  setFormTouched(true)
                }
              }}
            >
              <div
                className={`grid ${formFromProps?.fields?.length === 3 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4 mb-4`}
              >
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    // Render all except textarea in grid
                    if (Field && field.blockType !== 'textarea') {
                      return (
                        <div className="" key={index}>
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>
              {/* Textarea full width below */}
              <div className="">
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field && field.blockType === 'textarea') {
                      return (
                        <div key={index}>
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>
              {/* Turnstile widget */}
              <div className="mt-4">
                <TurnstileWidget
                  action="form_submit"
                  lazyLoad={true}
                  shouldLoad={formTouched}
                  onToken={(t) => setTsToken(t)}
                  onError={() => setTsToken(null)}
                  onExpire={() => setTsToken(null)}
                  resetSignal={tsResetKey}
                />
              </div>
              <div className="flex justify-center mt-4">
                <Button
                  form={formID}
                  type="submit"
                  disabled={!tsToken}
                  className="bg-primary text-primary-foreground rounded-full px-6 py-8 text-lg font-bold shadow transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-3 z-10">
                    {submitButtonLabel}
                    <ChevronsRight className="text-primary-foreground text-xl" />
                  </span>
                </Button>
              </div>
            </form>
          )}
        </FormProvider>
      </div>
    )
  }
  return (
    <BlockWrapper className="container mx-auto px-4 py-4 md:py-12">
      <div className="xl:flex -mx-4">
        <div className="xl:w-10/12 xl:mx-auto px-4">
          <div
            className={
              props.hideImageSection ? '' : 'md:flex md:-mx-4 gap-4 lg:gap-8 items-stretch'
            }
          >
            {/* Left: Image Section (optional) */}
            {!props.hideImageSection && (
              <div className="md:w-1/2 md:px-4 mb-6 md:mb-0">
                <figure className="w-full rounded-3xl overflow-hidden">
                  {(() => {
                    // Use the image field from config
                    const imgObj = props.image
                    let src: string | undefined = undefined
                    let alt: string = 'About'
                    if (imgObj) {
                      if (typeof imgObj === 'string') {
                        src = imgObj
                      } else if (typeof imgObj.url === 'string') {
                        src = imgObj.url
                        if (imgObj.caption) alt = imgObj.caption
                      }
                    }
                    src = src || '/fallback.jpg'
                    return (
                      <Image
                        src={src}
                        alt={alt}
                        width={1200}
                        height={480}
                        className="w-full h-64 sm:h-80 md:h-[28rem] lg:h-[30rem] object-cover rounded-3xl"
                        priority
                      />
                    )
                  })()}
                </figure>
              </div>
            )}
            {/* Right: Form Section */}
            <div className={`${props.hideImageSection ? 'w-full' : 'md:w-1/2'} md:px-4`}>
              <div className="bg-card rounded-2xl p-4 sm:p-6 shadow w-full">
                <div className="mb-6 md:mb-8 text-center md:text-left">
                  {props.formHeading && (
                    <RichText
                      data={props.formHeading}
                      enableProse={false}
                      className="text-2xl sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-2 text-foreground text-center"
                    />
                  )}
                  {props.formSubheading && (
                    <RichText
                      data={props.formSubheading}
                      enableProse={false}
                      className="text-sm sm:text-base md:text-lg text-muted-foreground mb-2 text-center"
                    />
                  )}
                </div>
                <FormProvider {...formMethods}>
                  {!isLoading && hasSubmitted && confirmationType === 'message' && (
                    <RichText data={confirmationMessage} />
                  )}
                  {isLoading && !hasSubmitted && (
                    <p className="text-lg text-foreground">Loading, please wait...</p>
                  )}
                  {error && (
                    <div className="text-destructive font-bold">{`${error.status || '500'}: ${error.message || ''}`}</div>
                  )}
                  {!hasSubmitted && (
                    <form
                      id={formID}
                      onSubmit={handleSubmit(onSubmit)}
                      onFocusCapture={() => {
                        if (!formTouched) {
                          setFormTouched(true)
                        }
                      }}
                    >
                      <div className="sm:flex sm:flex-wrap -mx-3">
                        {formFromProps &&
                          formFromProps.fields &&
                          formFromProps.fields?.map((field, index) => {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const Field: React.FC<any> =
                              fields?.[field.blockType as keyof typeof fields]
                            // Render all except textarea in flexible grid
                            if (Field && field.blockType !== 'textarea') {
                              return (
                                <div className="px-3 mb-4 sm:w-1/2" key={index}>
                                  <Field
                                    form={formFromProps}
                                    {...field}
                                    {...formMethods}
                                    control={control}
                                    errors={errors}
                                    register={register}
                                  />
                                </div>
                              )
                            }
                            return null
                          })}
                      </div>
                      {/* Textarea full width below */}
                      <div className="sm:flex sm:flex-wrap -mx-3 mb-4">
                        {formFromProps &&
                          formFromProps.fields &&
                          formFromProps.fields?.map((field, index) => {
                            const Field: React.FC<any> =
                              fields?.[field.blockType as keyof typeof fields]
                            if (Field && field.blockType === 'textarea') {
                              return (
                                <div className="px-3 mb-4 sm:w-full" key={index}>
                                  <Field
                                    form={formFromProps}
                                    {...field}
                                    {...formMethods}
                                    control={control}
                                    errors={errors}
                                    register={register}
                                  />
                                </div>
                              )
                            }
                            return null
                          })}
                      </div>
                      {/* Turnstile widget */}
                      <div className="px-3 mt-2">
                        <TurnstileWidget
                          action="form_submit"
                          lazyLoad={true}
                          shouldLoad={formTouched}
                          onToken={(t) => setTsToken(t)}
                          onError={() => setTsToken(null)}
                          onExpire={() => setTsToken(null)}
                          resetSignal={tsResetKey}
                        />
                      </div>

                      <div className="text-center sm:text-right mt-4 md:mt-6">
                        <Button
                          form={formID}
                          type="submit"
                          disabled={!tsToken}
                          className="bg-primary text-primary-foreground rounded-full px-6 py-6 sm:py-8 text-base sm:text-lg font-bold shadow transition-all duration-300 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <span className="flex items-center justify-center gap-3 z-10">
                            {submitButtonLabel}
                            <ChevronsRight className="text-primary-foreground text-lg sm:text-xl" />
                          </span>
                        </Button>
                      </div>
                    </form>
                  )}
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlockWrapper>
  )
}

export default FormBlock2
